import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Quote, PenLine, Send, X } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useLang } from "@/i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputStyle = {
  backgroundColor: "var(--bg-canvas)",
  borderColor: "var(--border-delicate)",
  color: "var(--text-primary)",
};

export default function Testimonials() {
  const { t } = useLang();
  const fallback = t("testimonials.quotes");
  const [quotes, setQuotes] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", text: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/testimonials`)
      .then((r) => setQuotes(r.data.length ? r.data : fallback))
      .catch(() => setQuotes(fallback));
  }, []);

  const list = quotes || fallback;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/testimonials/public`, form);
      toast.success(t("testimonials.reflectionSuccess"));
      setForm({ name: "", role: "", text: "" });
      setFormOpen(false);
    } catch (err) {
      toast.error(t("testimonials.reflectionError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="testimonials" data-testid="testimonials-section" className="px-5 py-24 sm:px-8 lg:py-32" style={{ backgroundColor: "var(--bg-surface)" }}>
      <div className="mx-auto max-w-7xl">
        <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>{t("testimonials.eyebrow")}</Eyebrow>
            <h2 className="font-display mt-6 max-w-2xl text-3xl font-light tracking-tight sm:text-4xl lg:text-5xl">
              {t("testimonials.h1")}<span className="italic" style={{ color: "var(--accent-gold)" }}>{t("testimonials.h2")}</span>
            </h2>
          </div>
          <button
            data-testid="share-reflection-button"
            onClick={() => setFormOpen((o) => !o)}
            className="flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-medium tracking-wide transition-transform hover:scale-[1.03]"
            style={{ borderColor: "var(--border-hover)", color: "var(--text-primary)" }}
          >
            {formOpen ? <X size={15} /> : <PenLine size={15} />}
            {t("testimonials.shareCta")}
          </button>
        </Reveal>

        {formOpen && (
          <Reveal>
            <form
              data-testid="reflection-form"
              onSubmit={submit}
              className="mb-12 rounded-3xl border p-7 sm:p-9"
              style={{ borderColor: "var(--border-hover)", backgroundColor: "var(--bg-canvas)" }}
            >
              <p className="font-display mb-6 text-xl italic" style={{ color: "var(--text-secondary)" }}>
                {t("testimonials.formTitle")}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <input data-testid="reflection-input-name" required value={form.name} onChange={set("name")}
                  className="rounded-xl border px-4 py-3 text-sm outline-none focus:border-[var(--border-hover)]" style={inputStyle}
                  placeholder={t("testimonials.namePh")} />
                <input data-testid="reflection-input-role" value={form.role} onChange={set("role")}
                  className="rounded-xl border px-4 py-3 text-sm outline-none focus:border-[var(--border-hover)]" style={inputStyle}
                  placeholder={t("testimonials.rolePh")} />
                <textarea data-testid="reflection-input-text" required rows={4} value={form.text} onChange={set("text")}
                  className="resize-none rounded-xl border px-4 py-3 text-sm outline-none focus:border-[var(--border-hover)] sm:col-span-2" style={inputStyle}
                  placeholder={t("testimonials.textPh")} />
              </div>
              <button
                data-testid="reflection-submit-button"
                type="submit"
                disabled={sending}
                className="mt-5 flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold tracking-wide transition-transform enabled:hover:scale-[1.03] disabled:opacity-60"
                style={{ backgroundColor: "var(--primary-btn-bg)", color: "var(--primary-btn-text)" }}
              >
                <Send size={14} />
                {sending ? t("contact.sending") : t("testimonials.submitReflection")}
              </button>
            </form>
          </Reveal>
        )}

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {list.map((q, i) => (
            <Reveal key={q.id || q.name} delay={i * 0.1}>
              <figure
                data-testid={`testimonial-card-${i + 1}`}
                className="flex h-full flex-col justify-between rounded-3xl border p-8"
                style={{ borderColor: "var(--border-delicate)", backgroundColor: "var(--bg-canvas)" }}
              >
                <div>
                  <Quote size={22} style={{ color: "var(--accent-gold)" }} fill="currentColor" strokeWidth={0} />
                  <blockquote className="mt-5 text-sm leading-relaxed sm:text-base" style={{ color: "var(--text-secondary)" }}>
                    {q.text}
                  </blockquote>
                </div>
                <figcaption className="mt-8 border-t pt-5" style={{ borderColor: "var(--border-delicate)" }}>
                  <p className="font-display text-lg">{q.name}</p>
                  <p className="mt-1 font-mono-alt text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>
                    {q.role}
                  </p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
